import {
	getAmountWithinRange,
	getDiscountAmount,
} from "../get-discount-amount";
import { bigMath } from "../misc";
import {
	DiscountInput,
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import {
	$deepPopulateItemMetadata,
	$getItemMetadata,
	$getOptionMetadata,
	$isOrderUnpaid,
	sortByAscendingSubtotal,
} from "./_internals";
import { calculateOrder } from "./calculate-order";
import { DISCOUNT_TYPE } from "~served/configs/enums";

export const applyDiscountToItem = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	item,
	discount,
}: {
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>;
	discount: DiscountInput;
}) => {
	const orderItem = $getItemMetadata(item);
	const { discountAmount } = getDiscountAmount(orderItem.subtotal, discount);
	const subtotal_addons = {
		...orderItem.subtotal_addons,
		discount: {
			...discount,
			is_divided: false,
			amount: discountAmount,
		},
	};

	let orderItemOptions = orderItem.options.map((orderItemOption) =>
		$getOptionMetadata(orderItemOption, orderItem),
	);
	orderItemOptions = orderItemOptions.map((orderItemOption) => {
		const optionPriceRatioToItem = bigMath.div(
			orderItemOption.subtotal,
			orderItem.subtotal,
		);

		return {
			...orderItemOption,
			subtotal_addons: {
				...orderItemOption.subtotal_addons,
				discount: {
					...discount,
					is_divided: true,
					amount: bigMath.mul(optionPriceRatioToItem, discountAmount),
				},
			},
		};
	});

	return {
		...orderItem,
		subtotal_addons,
		options: orderItemOptions,
	};
};

export const applyDiscountToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	discount,
	dividedAmountFromGroup,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	discount: DiscountInput;
	dividedAmountFromGroup?: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	if (discount?.type === DISCOUNT_TYPE.PERCENTAGE) {
		orderItems = orderItems.map((orderItem) => {
			const { discountAmount } = getDiscountAmount(
				orderItem.subtotal,
				discount,
			);
			const subtotal_addons = {
				...orderItem.subtotal_addons,
				discount: {
					...discount,
					is_divided: false,
					amount: discountAmount,
				},
			};

			return { ...orderItem, subtotal_addons };
		});
	}

	if (discount?.type === DISCOUNT_TYPE.AMOUNT) {
		const { discountAmount } = dividedAmountFromGroup
			? { discountAmount: dividedAmountFromGroup }
			: getDiscountAmount(subtotal, discount);

		let remainingAmountForDivision = discountAmount;
		orderItems = orderItems
			.sort(sortByAscendingSubtotal)
			.map((orderItem, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastItem = i === orderItems.length - 1;

				if (isThisLastItem) {
					let dividedDiscountAmountForItem = remainingAmountForDivision;

					if (dividedDiscountAmountForItem > (orderItem.subtotal || 0)) {
						dividedDiscountAmountForItem = orderItem.subtotal || 0;
					}

					const subtotal_addons = {
						...orderItem.subtotal_addons,
						discount: {
							...discount,
							is_divided: true,
							amount: dividedDiscountAmountForItem,
						},
					};

					remainingAmountForDivision = bigMath.sub(
						remainingAmountForDivision,
						dividedDiscountAmountForItem,
					);

					return { ...orderItem, subtotal_addons };
				}

				const itemPriceRatioToOrder = bigMath.div(orderItem.subtotal, subtotal);
				let dividedDiscountAmountForItem = isThereAmountToDivide
					? bigMath.mul(discountAmount, itemPriceRatioToOrder)
					: 0;
				dividedDiscountAmountForItem =
					dividedDiscountAmountForItem > (orderItem.subtotal || 0)
						? orderItem.subtotal || 0
						: dividedDiscountAmountForItem;

				const subtotal_addons = {
					...orderItem.subtotal_addons,
					discount: {
						...discount,
						is_divided: true,
						amount: dividedDiscountAmountForItem,
					},
				};

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForItem,
				);

				return { ...orderItem, subtotal_addons };
			});

		if (remainingAmountForDivision > 0) {
			orderItems = orderItems.map((orderItem) => {
				if (
					orderItem.subtotal_addons.discount.amount === orderItem.subtotal ||
					remainingAmountForDivision === 0
				) {
					return orderItem;
				}

				const subtotalDiff = bigMath.sub(
					orderItem.subtotal,
					orderItem.subtotal_addons.discount.amount,
				);
				const addedAmount =
					subtotalDiff > remainingAmountForDivision
						? remainingAmountForDivision
						: subtotalDiff;

				const subtotal_addons = {
					...orderItem.subtotal_addons,
					discount: {
						...orderItem.subtotal_addons.discount,
						amount: bigMath.add(
							orderItem.subtotal_addons.discount.amount,
							addedAmount,
						),
					},
				};

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					addedAmount,
				);

				return { ...orderItem, subtotal_addons };
			});
		}
	}

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const optionPriceRatioToItem = bigMath.div(
				orderItemOption.subtotal,
				orderItem.subtotal,
			);
			let amount = bigMath.mul(
				optionPriceRatioToItem,
				orderItem.subtotal_addons.discount.amount,
			);
			amount = getAmountWithinRange(0, amount, orderItemOption.subtotal);

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					discount: {
						...discount,
						is_divided: true,
						amount,
					},
				},
			};
		}),
	}));

	const discount_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.discount.amount || 0),
		0,
	);

	return calculateOrder({
		...order,
		items: orderItems,
		subtotal,
		discount_amount,
	}).$order;
};

export const applyDiscountToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders: allOrders,
	discount,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	discount: DiscountInput;
	skipPaidCheck?: boolean;
}) => {
	const unpaidOrders = allOrders.filter((o) =>
		skipPaidCheck ? true : $isOrderUnpaid(o),
	);
	let updatedOrders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[] =
		[];

	if (discount?.type === DISCOUNT_TYPE.PERCENTAGE) {
		updatedOrders = unpaidOrders.map((order) =>
			applyDiscountToOrder({ order, discount }),
		);
	}

	if (discount?.type === DISCOUNT_TYPE.AMOUNT) {
		const ordersGroupSubtotal = unpaidOrders.reduce(
			(pre, cur) => bigMath.add(pre, cur.subtotal),
			0,
		);
		const { discountAmount } = getDiscountAmount(ordersGroupSubtotal, discount);

		// For 100% off discounts, we want to distribute the full discount amount
		// For other discounts, cap to not exceed the combined orders subtotal
		const isFullDiscount = discountAmount >= ordersGroupSubtotal;
		const targetDiscountAmount = isFullDiscount
			? discountAmount
			: Math.min(discountAmount, ordersGroupSubtotal);
		let remainingAmountForDivision = targetDiscountAmount;

		updatedOrders = unpaidOrders
			// @ts-ignore
			.sort(sortByAscendingSubtotal)
			.map((order, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastItem = i === unpaidOrders.length - 1;

				if (isThisLastItem) {
					// For the last item, use all remaining amount, but don't exceed order subtotal
					const finalAmount = Math.min(
						remainingAmountForDivision,
						order.subtotal || 0,
					);
					return applyDiscountToOrder({
						order,
						discount,
						dividedAmountFromGroup: isThereAmountToDivide ? finalAmount : 0,
					});
				}

				const orderPriceRatioToGroup = bigMath.div(
					order.subtotal,
					ordersGroupSubtotal,
				);
				let dividedDiscountAmountForOrder = isThereAmountToDivide
					? bigMath.mul(targetDiscountAmount, orderPriceRatioToGroup)
					: 0;

				// Ensure the divided amount doesn't exceed the order subtotal
				dividedDiscountAmountForOrder = Math.min(
					dividedDiscountAmountForOrder,
					order.subtotal || 0,
				);

				// Ensure we don't exceed the remaining amount to divide
				dividedDiscountAmountForOrder = Math.min(
					dividedDiscountAmountForOrder,
					remainingAmountForDivision,
				);

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForOrder,
				);

				return applyDiscountToOrder({
					order,
					discount,
					dividedAmountFromGroup: dividedDiscountAmountForOrder,
				});
			});

		// If there's still remaining amount and this is a full discount, distribute it
		if (remainingAmountForDivision > 0 && isFullDiscount) {
			// Find orders that can still accept more discount amount
			const ordersWithRemainingCapacity = updatedOrders
				.map((order, index) => ({
					order,
					index,
					remainingCapacity: Math.max(
						0,
						(unpaidOrders[index].subtotal || 0) -
							calculateOrder(order).discount_amount,
					),
				}))
				.filter((item) => item.remainingCapacity > 0);

			// Distribute remaining amount among orders with capacity
			let remainingToDistribute = remainingAmountForDivision;
			for (const item of ordersWithRemainingCapacity) {
				if (remainingToDistribute <= 0) break;

				const amountToAdd = Math.min(
					remainingToDistribute,
					item.remainingCapacity,
				);
				const currentOrder = updatedOrders[item.index];

				// Recalculate the order with the additional discount amount
				const recalculatedOrder = applyDiscountToOrder({
					order: unpaidOrders[item.index],
					discount,
					dividedAmountFromGroup:
						calculateOrder(currentOrder).discount_amount + amountToAdd,
				});

				updatedOrders[item.index] = recalculatedOrder;
				remainingToDistribute -= amountToAdd;
			}
		}
	}

	return updatedOrders;
};
